import { mdiPlus } from '@mdi/js'
import Icon from '@mdi/react'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CommentTextBox, { FacebookCommentTextBox } from 'components/Composer/Network/CommentTextBox'
import TextEditor from 'components/TextEditor'
import { useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { setMultiPostError } from 'services/compose'
import { YOUTUBE_TITLE_TOO_LONG_ERROR_CODE } from 'services/compose/multi'
import { BulkContentPost } from 'services/compose/state'
import { blueskyUsersSearch, fbLiveSearch, linkedInLiveSearch } from 'services/search/actions'
import { currentUserSelector } from 'services/users/selectors'
import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  MAX_BLUESKY_STATUS_LENGTH,
  MAX_YT_TITLE_LENGTH
} from 'shared/constants'
import {
  ComposerFacebookPost,
  ComposerInstagramPost,
  ComposerPinterestPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  PostDestination,
  PostDestinationType
} from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import styles from './BulkPost.pcss'
import { BulkPostYoutubeStatus } from './BulkPostYoutubeStatus'
import { PinterestPostAttributes } from './PinterestPostAttributes'
import { TikTokPostStatus } from './TikTokPostStatus'

const YT_TITLE_LENGTH_ERROR = {
  code: YOUTUBE_TITLE_TOO_LONG_ERROR_CODE,
  message: 'YouTube post titles are limited to a maximum of 100 characters.'
}

interface BulkPostStatusProps {
  post: BulkContentPost
  profiles: PostDestination[]
  allowStatusOverride: boolean
  tabIndex?: number
  onStatusChange: (postId: string, text: string, html: string, network: PostDestinationType) => void
  onPinChange: (postId: string, data: { title?: string, description?: string, destinationUrl?: string, applyToAll?: boolean }) => void
  onYtStatusChange: (postId: string, data: { title?: string, description?: string }) => void
  onTikTokPhotoStatusChange: (postId: string, data: { title?: string, description?: string }) => void
  onAddComment: (postId: string, network: PostDestinationType) => void
  onCopyCommentToAll: (postId: string, commentId: string) => void
}

export function BulkPostStatus(props: BulkPostStatusProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const user = useSelector(currentUserSelector)
  const { post, profiles } = props
  const { data, id, network } = post
  const isImage = Boolean(post.imageUrl)

  const postErrorRef = useRef(post.error)
  postErrorRef.current = post.error

  const pin = network === BRAND_PINTEREST ? { ...data } as ComposerPinterestPost : null
  const ytPost = network === BRAND_YOUTUBE ? { ...data } as ComposerYoutubePost : null
  if (pin) {
    pin.title ??= ''
    pin.description = pin.description || ''
    pin.destinationUrl ??= ''
  }
  if (ytPost) {
    ytPost.title ??= ''
    ytPost.description = ytPost.description || ''
  }

  const searchFacebookTags = (query: string) => dispatch(fbLiveSearch(query))
  const searchLinkedinTags = (query: string) => dispatch(linkedInLiveSearch(query))
  const searchBlueskyTags = (query: string) => dispatch(blueskyUsersSearch(query, profiles[0].ownerId))

  let tagSearchFn
  switch (network) {
    case BRAND_FACEBOOK:
      tagSearchFn = searchFacebookTags
      break
    case BRAND_LINKEDIN:
      tagSearchFn = searchLinkedinTags
      break
    case BRAND_BLUESKY:
      // The search needs a profile owner id — without profiles there is nothing to search against.
      tagSearchFn = profiles.length > 0 ? searchBlueskyTags : undefined
      break
    default:
      tagSearchFn = undefined
  }

  const onStatusChange = (text: string, html: string) => {
    props.onStatusChange(id, text, html, network)
  }

  const onTikTokVideoCaptionChange = (text: string) => {
    props.onStatusChange(id, text, text, network)
  }

  const onTikTokPhotoStatusChange = ({ title, description }: { title?: string, description?: string }) => {
    const statusData = typeof title === 'string' ? { title } : { description }
    props.onTikTokPhotoStatusChange(id, statusData)
  }

  const onPinTitleChange = (val: string) => {
    props.onPinChange(id, { title: val })
  }

  const onPinDescriptionChange = (val: string) => {
    props.onPinChange(id, { description: val })
  }

  const onPinLinkChange = (val: string) => {
    props.onPinChange(id, { destinationUrl: val })
  }

  const applyPinTitleToAll = () => {
    props.onPinChange(id, { title: pin!.title, applyToAll: true })
  }

  const applyPinUrlToAll = () => {
    props.onPinChange(id, { destinationUrl: pin!.destinationUrl, applyToAll: true })
  }

  const onYtTitleChange = (val: string) => {
    // The title field is debounced, so read the error through a ref - the closure can be older than the post.
    if (val.length > MAX_YT_TITLE_LENGTH && !postErrorRef.current) {
      dispatch(setMultiPostError({ postId: id, error: YT_TITLE_LENGTH_ERROR }))
    }
    if (postErrorRef.current?.code === YOUTUBE_TITLE_TOO_LONG_ERROR_CODE && val.length <= MAX_YT_TITLE_LENGTH) {
      dispatch(setMultiPostError({ postId: id, error: undefined }))
    }
    props.onYtStatusChange(id, { title: val })
  }

  const onYtDescriptionChange = (val: string) => {
    props.onYtStatusChange(id, { description: val })
  }

  const handleAddComment = () => {
    props.onAddComment(id, network)
  }

  const renderCopyCommentToAll = (commentId: string) => (
    <span role="button" className={styles['txt-btn']} onClick={() => props.onCopyCommentToAll(id, commentId)}>
      <FormattedMessage id="actions.copy-to-all" />
    </span>
  )

  const isRetweet = network === BRAND_TWITTER && (data as ComposerTwitterPost).tweetType === 'retweet'
  const restrictedCommentsNetworks = user?.roles?.hasMultipleComments ? [BRAND_THREADS, BRAND_TWITTER, BRAND_YOUTUBE] : []
  const withComments = [BRAND_INSTAGRAM, BRAND_LINKEDIN, BRAND_BLUESKY, ...restrictedCommentsNetworks].includes(network)
  const isNetworkCommentsLimited = [BRAND_TWITTER, BRAND_YOUTUBE, BRAND_THREADS].includes(network)
  const withCopyCommentToAll = [BRAND_INSTAGRAM, BRAND_FACEBOOK].includes(network)

  return (
    <div className={`${styles['status-box']} ${withCopyCommentToAll ? styles['with-copy-to-all'] : ''}`}>
      {pin && (
        <PinterestPostAttributes
          pin={pin}
          onTitleChange={onPinTitleChange}
          onDescriptionChange={onPinDescriptionChange}
          onDestinationUrlChange={onPinLinkChange}
          applyPinTitleToAll={applyPinTitleToAll}
          applyPinUrlToAll={applyPinUrlToAll}
        />
      )}
      {network === BRAND_TIKTOK && (
        <TikTokPostStatus
          videoCaption={(data as ComposerTikTokPost).status.text}
          photoTitle={(data as ComposerTikTokPost).title}
          photoDescription={(data as ComposerTikTokPost).description}
          isPhotoPost={isImage}
          onCaptionChange={onTikTokVideoCaptionChange}
          onPhotoStatusChange={onTikTokPhotoStatusChange}
        />
      )}
      {ytPost && (
        <BulkPostYoutubeStatus
          post={ytPost}
          onTitleChange={onYtTitleChange}
          onDescriptionChange={onYtDescriptionChange}
        />
      )}
      {![BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(network) && (
        <div style={{ position: 'relative' }}>
          <TextEditor
            network={network}
            placeholder={intl.formatMessage({ id: 'composer.multi-post.placeholders.status' })}
            html={props.allowStatusOverride ? data.status.html : undefined}
            initialHTML={data.status.html}
            inputClassName={`${styles['text-editor']} ${isRetweet ? styles.hidden : ''}`}
            containerClassName={styles['text-editor-container']}
            withEmojis
            withGenerateText
            footerClassName={styles['text-editor-footer']}
            tabIndex={props.tabIndex}
            disabled={isRetweet}
            onChange={onStatusChange}
            searchTags={tagSearchFn}
          />
        </div>
      )}
      {withComments && (
        <div className={styles['comment-box']}>
          {(data as ComposerInstagramPost).comments.map((comment, index) => (
            <div key={comment.id} className={styles['comment-input-row']}>
              <CommentTextBox
                commentId={comment.id}
                delay={comment.delay}
                network={network as any}
                initialValue={comment.text}
                multiPostId={id}
                showRemoveButton={(data as ComposerInstagramPost).comments.length > 1}
                maxLength={network === BRAND_BLUESKY ? MAX_BLUESKY_STATUS_LENGTH : undefined}
                className={styles['first-comment-container']}
                actionsClassName={styles['first-comment-actions']}
                isFirstComment={index === 0}
              />
              {withCopyCommentToAll && renderCopyCommentToAll(comment.id)}
            </div>
          ))}
          {(!isNetworkCommentsLimited || user?.roles?.hasMultipleComments) && (
            <Tooltip title="Add additional comment" placement="right">
              <IconButton size="small" className={styles.addCommentButton} onClick={handleAddComment}>
                <Icon path={mdiPlus} size="24px" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}
      {network === BRAND_FACEBOOK && (
        <div className={styles['comment-box']}>
          {(data as ComposerFacebookPost).comments.map((comment) => (
            <div key={comment.id} className={styles['comment-input-row']}>
              <FacebookCommentTextBox
                comment={comment}
                tagSearchFn={tagSearchFn as any}
                multiPostId={id}
                showRemoveButton={(data as ComposerFacebookPost).comments.length > 1}
                className={styles['fb-comment-container']}
              />
              {renderCopyCommentToAll(comment.id)}
            </div>
          ))}
          <Tooltip title="Add additional comment" placement="right">
            <IconButton size="small" className={styles.addCommentButton} onClick={handleAddComment}>
              <Icon path={mdiPlus} size="24px" />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
